// Sync To Cloud Tool - Uploads app files (including .git) to backend API
// Extends WorkspaceTool - has access to appType and toolSystem
// This is the reverse of SyncFromCloudTool - pushes local files to cloud storage

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";
import { notifyWorkspaceServiceWorker } from "../../../../../../workspace/serviceWorkerBridge.js";
import JSZip from "jszip";

export class SyncToCloudTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'syncToCloudTool';
  }

  async buildGitMetadata(dirPath = "/files") {
    if (!this.git || !this.fs) {
      return {
        version: 1,
        hasGit: false,
        head: null,
        timestamp: null,
        reachableOids: [],
        commitCount: 0,
      };
    }

    try {
      const head = await this.git.resolveRef({
        fs: this.fs,
        dir: dirPath,
        ref: "HEAD",
      });
      const commits = await this.git.log({
        fs: this.fs,
        dir: dirPath,
        ref: head,
        depth: 2000,
      });
      const timestampSeconds = commits?.[0]?.commit?.author?.timestamp || null;
      const reachableOids = commits
        .map((commit) => commit?.oid)
        .filter((oid) => typeof oid === "string" && oid);

      return {
        version: 1,
        hasGit: true,
        head,
        timestamp: timestampSeconds ? timestampSeconds * 1000 : null,
        reachableOids,
        commitCount: reachableOids.length,
      };
    } catch (error) {
      return {
        version: 1,
        hasGit: false,
        head: null,
        timestamp: null,
        reachableOids: [],
        commitCount: 0,
      };
    }
  }

  async stageGitMetadata(appId, metadata) {
    const response = await fetch(`/api/apps/${appId}/workspace/stage-git-metadata`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ metadata }),
    });

    if (!response.ok) {
      const text = await response.text();
      let errorMessage = `Failed to upload git metadata (${response.status})`;

      try {
        const errorData = JSON.parse(text);
        if (errorData?.error) {
          errorMessage = errorData.error;
        }
      } catch (parseError) {
        if (text?.trim()) {
          errorMessage = text;
        }
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }

  async stageAppBundle(appId, bundleBlob, type = "files") {
    const response = await fetch(
      `/api/apps/${appId}/workspace/stage-bundle?type=${encodeURIComponent(type)}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/zip",
          "X-Sync-Timing": "1",
        },
        credentials: "include",
        body: bundleBlob,
      }
    );

    if (!response.ok) {
      const text = await response.text();
      let errorMessage = `Failed to stage ${type} bundle (${response.status})`;

      try {
        const errorData = JSON.parse(text);
        if (errorData?.error) {
          errorMessage = errorData.error;
        }
      } catch (parseError) {
        if (text?.trim()) {
          errorMessage = text;
        }
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }

  async finalizeAppBundle(appId, type = "files") {
    const response = await fetch(
      `/api/apps/${appId}/workspace/finalize?type=${encodeURIComponent(type)}`,
      {
        method: "POST",
        credentials: "include",
      }
    );

    if (!response.ok) {
      const text = await response.text();
      let errorMessage = `Failed to finalize ${type} bundle (${response.status})`;

      try {
        const errorData = JSON.parse(text);
        if (errorData?.error) {
          errorMessage = errorData.error;
        }
      } catch (parseError) {
        if (text?.trim()) {
          errorMessage = text;
        }
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }

  async stageChatsBundle(membershipId, bundleBlob) {
    const response = await fetch(
      `/api/app-memberships/${membershipId}/chats/stage-bundle`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/zip",
          "X-Sync-Timing": "1",
        },
        credentials: "include",
        body: bundleBlob,
      }
    );

    if (!response.ok) {
      const text = await response.text();
      let errorMessage = `Failed to stage chats bundle (${response.status})`;

      try {
        const errorData = JSON.parse(text);
        if (errorData?.error) {
          errorMessage = errorData.error;
        }
      } catch (parseError) {
        if (text?.trim()) {
          errorMessage = text;
        }
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }

  async finalizeChatsBundle(membershipId) {
    const response = await fetch(
      `/api/app-memberships/${membershipId}/chats/finalize`,
      {
        method: "POST",
        credentials: "include",
      }
    );

    if (!response.ok) {
      const text = await response.text();
      let errorMessage = `Failed to finalize chats bundle (${response.status})`;

      try {
        const errorData = JSON.parse(text);
        if (errorData?.error) {
          errorMessage = errorData.error;
        }
      } catch (parseError) {
        if (text?.trim()) {
          errorMessage = text;
        }
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Sync files from local filesystem to cloud storage
   * @param {string} appId - App ID to sync files for
   * @param {Object} [workspaceManager] - Optional workspace manager instance
   * @param {Function} [onProgress] - Optional progress callback: (progress, message) => void
   * @returns {Promise<Object>} Result with operation status and file count
   */
  async syncToCloud(appId, workspaceManager = null, onProgress = null) {
    try {
      // Validate appId
      if (!appId || typeof appId !== "string") {
        return this.returnError({}, "Valid app ID required");
      }

      // Route based on app type
      if (this.appType === "browser") {
        return await this.syncToCloudForBrowser(appId, workspaceManager, onProgress);
      } else {
        return this.returnError(
          {},
          `App type '${this.appType}' not yet implemented for syncToCloud`
        );
      }
    } catch (error) {
      logger.error(
        {
          tags: "tools|synctocloud|error",
          color1: "red",
        },
        `Failed to sync files to cloud for app: ${appId}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Sync files to cloud for browser apps using Lightning FS
   */
  async syncToCloudForBrowser(appId, workspaceManager, onProgress = null) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    logger.log(
      {
        tags: 'workspace|sync-to-cloud|info',
        color1: 'lime'
      },
      "syncToCloudForBrowser started"
    );
    const t0 = performance.now();
    const timings = { fileCount: 0, zipSizeBytes: 0 };

    logger.log(
      {
        tags: "workspace|sync-to-cloud|info",
        color1: "lime",
      },
      `Starting file sync to cloud for app: ${appId}`
    );

    // Progress: Starting sync
    onProgress?.(5, "Preparing sync...");

    // Get all files from Lightning FS /files/ directory
    const { files: localFiles, errors: scanErrors } = await this.getAllFiles("/files", fs);
    const t1 = performance.now();
    timings.getAllFilesMs = Math.round(t1 - t0);
    timings.fileCount = localFiles.length;

    if (scanErrors.length > 0) {
      this._logSyncTimingBreakdown(timings, "failed after getAllFiles (scan errors)");
      return this.returnError(
        { errorType: "filesystem_scan", errors: scanErrors },
        "Failed to scan all files before sync"
      );
    }

    // Progress: File scanning complete
    onProgress?.(20, `Found ${localFiles.length} files to sync...`);

    if (localFiles.length === 0) {
      logger.log(
        {
          tags: "workspace|sync-to-cloud|info",
          color1: "lime",
        },
        `No files found for app: ${appId}, will send empty zip to clear cloud storage`
      );
    }

    // Build zip from local files (frontend builds zip; backend forwards to Spaces)
    const zip = new JSZip();

    for (const filepath of localFiles) {
      try {
        const filesystemPath = `/files/${filepath}`;
        const buffer = await fs.readFile(filesystemPath);
        zip.file(filepath, buffer);
      } catch (error) {
        timings.readFilesAndAddToZipMs = Math.round(performance.now() - t1);
        this._logSyncTimingBreakdown(timings, "failed during readFilesAndAddToZip");
        logger.warn(
          {
            tags: "workspace|sync-to-cloud|read",
            color1: "yellow",
          },
          `Failed to read file: ${filepath}`,
          error
        );
        return this.returnError(
          { errorType: "filesystem_read", path: filepath, error: error.message },
          "Failed to read all files before sync"
        );
      }
    }
    const t2 = performance.now();
    timings.readFilesAndAddToZipMs = Math.round(t2 - t1);

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const t3 = performance.now();
    timings.zipGenerateMs = Math.round(t3 - t2);
    timings.zipSizeBytes = zipBlob.size;

    // Progress: Archive creation complete
    const sizeMB = (zipBlob.size / (1024 * 1024)).toFixed(1);
    onProgress?.(70, `Created ${sizeMB}MB archive...`);

    try {
      // Progress: Starting upload
      onProgress?.(80, "Uploading bundle to cloud staging...");

      const t4 = performance.now();
      await this.stageAppBundle(appId, zipBlob, "files");
      const t5 = performance.now();
      timings.fetchUploadMs = Math.round(t5 - t4);
      const t6 = performance.now();
      timings.parseResponseMs = Math.round(t6 - t5);

      onProgress?.(85, "Staging git metadata...");
      try {
        const gitMetadata = await this.buildGitMetadata("/files");
        await this.stageGitMetadata(appId, gitMetadata);
      } catch (gitMetadataError) {
        this._logSyncTimingBreakdown(
          timings,
          `failed during git metadata staging: ${gitMetadataError.message}`
        );
        return this.returnError(
          {
            errorType: "git_metadata_upload_failed",
            partialSuccess: true,
          },
          `Workspace bundle staged, but git metadata staging failed: ${gitMetadataError.message}`
        );
      }

      onProgress?.(92, "Finalizing cloud sync...");
      try {
        const finalizeResponse = await this.finalizeAppBundle(appId, "files");
        if (finalizeResponse?.debugTiming) {
          timings.backend = finalizeResponse.debugTiming;
        }
      } catch (finalizeError) {
        this._logSyncTimingBreakdown(
          timings,
          `failed during finalize: ${finalizeError.message}`
        );
        return this.returnError(
          {
            errorType: "finalize_failed",
            partialSuccess: true,
          },
          `Workspace bundle and metadata were staged, but finalization failed: ${finalizeError.message}`
        );
      }

      // Notify service worker of the sync
      notifyWorkspaceServiceWorker({
        type: "WORKSPACE_APP_UPDATED",
        appId,
      });

      const t7 = performance.now();
      timings.postSyncLocalMs = Math.round(t7 - t6);

      // Progress: files are done; chats/assets may still be syncing
      onProgress?.(95, "Files synced; syncing chats/assets...");

      logger.log(
        {
          tags: "workspace|sync-to-cloud|info",
          color1: "lime",
        },
        `File sync to cloud completed for app: ${appId}`,
        {
          totalFiles: localFiles.length,
          zipSize: zipBlob.size,
        }
      );

      // Sync chats to user-specific membership storage; surface limit errors like files sync
      const membershipId = workspaceManager?.getMembershipId?.();
      if (membershipId) {
        const t8 = performance.now();
        const chatsResult = await this.syncChatsToCloud(membershipId, fs);
        const t9 = performance.now();
        timings.chatsSyncMs = Math.round(t9 - t8);
        if (chatsResult && !chatsResult.success) {
          timings.totalMs = Math.round(t9 - t0);
          this._logSyncTimingBreakdown(timings, null);
          return chatsResult;
        }
      } else {
        timings.chatsSyncMs = 0;
        logger.warn(
          { tags: "workspace|sync-to-cloud|chats", color1: "yellow" },
          "Skipping chat sync: missing membershipId"
        );
      }

      const t10 = performance.now();
      const assetsResult = await this.syncAssetsToCloud(appId, fs);
      const t11 = performance.now();
      timings.assetsSyncMs = Math.round(t11 - t10);
      if (assetsResult && !assetsResult.success) {
        timings.totalMs = Math.round(t11 - t0);
        this._logSyncTimingBreakdown(timings, null);
        return assetsResult;
      }

      timings.totalMs = Math.round(performance.now() - t0);
      this._logSyncTimingBreakdown(timings, null);

      // Progress: Sync complete (all components finished: files + chats + assets)
      onProgress?.(100, "Sync complete!");

      return this.returnSuccess(
        {
          appId,
          fileCount: localFiles.length,
          files: localFiles,
        },
        `Synced ${localFiles.length} files to cloud (zip uploaded)`
      );
    } catch (error) {
      timings.totalMs = Math.round(performance.now() - t0);
      this._logSyncTimingBreakdown(timings, error.message || "exception");
      logger.error(
        {
          tags: "workspace|sync-to-cloud|error",
          color1: "red",
        },
        "Failed to upload files to cloud",
        error
      );

      // Extract error message, handling both parsed errors and JSON strings
      let errorMessage = error.message || "Failed to upload files to cloud";

      // If error.message is a JSON string, try to parse it
      if (errorMessage && errorMessage.trim().startsWith("{")) {
        try {
          const errorData = JSON.parse(errorMessage);
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.errorCode === "STORAGE_LIMIT_EXCEEDED") {
            errorMessage = errorData.error || "Storage limit exceeded. Please upgrade your plan or delete some files.";
          } else if (errorData.errorCode === "BUNDLE_SIZE_LIMIT_EXCEEDED") {
            errorMessage = errorData.error || "Bundle size exceeds the maximum allowed size of 5 GB. Please reduce the number or size of files.";
          }
        } catch (parseError) {
          // If parsing fails, use the message as-is
        }
      }

      return this.returnError({}, errorMessage);
    }
  }

  /**
   * Log [SYNCTIMINGTEST] breakdown for debugging sync performance (filterable, copy-paste friendly).
   * @param {Object} timings - { getAllFilesMs, readFilesAndAddToZipMs, zipGenerateMs, fetchUploadMs, parseResponseMs, postSyncLocalMs, chatsSyncMs, totalMs, fileCount, zipSizeBytes, backend? }
   * @param {string|null} failedAt - If set, append "(failed at: ...)" to the header.
   */
  _logSyncTimingBreakdown(timings, failedAt) {
    const lines = [
      "Sync to cloud timing breakdown (ms):",
      `  getAllFiles: ${timings.getAllFilesMs ?? "—"}`,
      `  readFilesAndAddToZip: ${timings.readFilesAndAddToZipMs ?? "—"}`,
      `  zipGenerate: ${timings.zipGenerateMs ?? "—"}`,
      `  fetchUpload: ${timings.fetchUploadMs ?? "—"}`,
      `  parseResponse: ${timings.parseResponseMs ?? "—"}`,
      `  postSyncLocal: ${timings.postSyncLocalMs ?? "—"}`,
      `  chatsSync: ${timings.chatsSyncMs ?? "—"}`,
      `  assetsSync: ${timings.assetsSyncMs ?? "—"}`,
      `  total: ${timings.totalMs ?? "—"}`,
      `  fileCount: ${timings.fileCount ?? "—"}`,
      `  zipSizeBytes: ${timings.zipSizeBytes ?? "—"}`,
    ];
    if (timings.backend && typeof timings.backend === "object") {
      lines.push("  Backend (ms):");
      Object.entries(timings.backend).forEach(([k, v]) => {
        lines.push(`    ${k}: ${v}`);
      });
    }
    if (failedAt) {
      lines[0] = `Sync to cloud timing breakdown (ms) — partial (failed at: ${failedAt}):`;
    }
    const block = lines.join("\n");
    logger.log({ tags: "workspace|sync-to-cloud|timing", color1: "cyan" }, block);
  }

  /**
   * Sync chat files to cloud for a specific app membership (zip bundle, same pattern as files sync)
   * @param {string} membershipId - App membership ID
   * @param {Object} fs - Filesystem promises interface
   */
  async syncChatsToCloud(membershipId, fs) {
    try {
      logger.log(
        {
          tags: "workspace|sync-to-cloud|chats",
          color1: "lime",
        },
        `Starting chat sync to cloud for membership: ${membershipId}`
      );

      const { files: localChatFiles, errors: chatScanErrors } = await this.getAllFiles("/chats", fs);
      if (chatScanErrors.length > 0) {
        logger.warn(
          { tags: "tools|synctocloud|chats", color1: "yellow" },
          "Skipping chat sync due to filesystem scan errors",
          chatScanErrors
        );
        return;
      }

      const zip = new JSZip();
      for (const filepath of localChatFiles) {
        try {
          const filesystemPath = `/chats/${filepath}`;
          const buffer = await fs.readFile(filesystemPath);
          zip.file(filepath, buffer);
        } catch (error) {
          logger.warn(
            {
              tags: "tools|synctocloud|chats|read",
              color1: "yellow",
            },
            `Failed to read chat file: ${filepath}`,
            error
          );
          return this.returnError(
            { errorType: "filesystem_read", path: filepath, error: error.message },
            "Failed to read all chat files before sync"
          );
        }
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });

      await this.stageChatsBundle(membershipId, zipBlob);
      await this.finalizeChatsBundle(membershipId);

      logger.log(
        {
          tags: "tools|synctocloud|chats",
          color1: "lime",
        },
        `Chat sync to cloud completed for membership: ${membershipId}`,
        { totalFiles: localChatFiles.length }
      );
    } catch (error) {
      logger.warn(
        {
          tags: "tools|synctocloud|chats|error",
          color1: "yellow",
        },
        "Failed to sync chats to cloud",
        error
      );
      throw error;
    }
  }

  async syncAssetsToCloud(appId, fs) {
    try {
      logger.log(
        {
          tags: "workspace|sync-to-cloud|assets",
          color1: "lime",
        },
        `Starting assets sync to cloud for app: ${appId}`
      );

      const { files: localAssetFiles, errors: assetScanErrors } = await this.getAllFiles(
        "/assets",
        fs
      );
      if (assetScanErrors.length > 0) {
        logger.warn(
          { tags: "tools|synctocloud|assets", color1: "yellow" },
          "Skipping assets sync due to filesystem scan errors",
          assetScanErrors
        );
        return;
      }

      const zip = new JSZip();
      for (const filepath of localAssetFiles) {
        try {
          const filesystemPath = `/assets/${filepath}`;
          const buffer = await fs.readFile(filesystemPath);
          zip.file(filepath, buffer);
        } catch (error) {
          logger.warn(
            {
              tags: "tools|synctocloud|assets|read",
              color1: "yellow",
            },
            `Failed to read asset file: ${filepath}`,
            error
          );
          return this.returnError(
            { errorType: "filesystem_read", path: filepath, error: error.message },
            "Failed to read all asset files before sync"
          );
        }
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });

      await this.stageAppBundle(appId, zipBlob, "assets");
      await this.finalizeAppBundle(appId, "assets");

      logger.log(
        {
          tags: "tools|synctocloud|assets",
          color1: "lime",
        },
        `Assets sync to cloud completed for app: ${appId}`,
        { totalFiles: localAssetFiles.length }
      );
    } catch (error) {
      logger.warn(
        {
          tags: "tools|synctocloud|assets|error",
          color1: "yellow",
        },
        "Failed to sync assets to cloud",
        error
      );
      throw error;
    }
  }

  /**
   * Recursively get all files in the filesystem (including .git)
   */
  async getAllFiles(dir, fs, basePath = "") {
    const files = [];
    const errors = [];

    try {
      const entries = await fs.readdir(dir === "/" ? "/" : dir);

      for (const entry of entries) {
        // Include .git and .revolt directories in sync
        const fullPath = dir === "/" ? `/${entry}` : `${dir}/${entry}`;
        const relativePath = basePath ? `${basePath}/${entry}` : entry;

        try {
          const stats = await fs.stat(fullPath);
          const isDir =
            typeof stats.isDirectory === "function"
              ? stats.isDirectory()
              : stats.type === "dir";

          if (isDir) {
            const { files: subFiles, errors: subErrors } = await this.getAllFiles(
              fullPath,
              fs,
              relativePath
            );
            files.push(...subFiles);
            if (subErrors.length > 0) {
              errors.push(...subErrors);
            }
          } else {
            files.push(relativePath);
          }
        } catch (error) {
          errors.push({ path: fullPath, error: error.message });
        }
      }
    } catch (error) {
      errors.push({ path: dir, error: error.message });
    }

    return { files, errors };
  }

  /**
   * Encode buffer to base64
   */
  encodeBase64(buffer) {
    if (typeof btoa === "function") {
      const binary = Array.from(buffer)
        .map((byte) => String.fromCharCode(byte))
        .join("");
      return btoa(binary);
    }
    if (typeof Buffer !== "undefined") {
      return Buffer.from(buffer).toString("base64");
    }
    throw new Error("Unable to encode content as base64");
  }
}
